// src/components/admin/AdminProductEdit.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../lib/AppContext';

const LANGS = ['en','de','sk','hu','ro','pl','hr','nl'];
const BADGES = [{ v:'', l:'None' },{ v:'hot', l:'🔥 Bestseller' },{ v:'new', l:'✨ New' },{ v:'sale', l:'🏷 Sale' }];

export default function AdminProductEdit() {
  const { id } = useParams();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { showToast } = useApp();
  const [categories, setCategories] = useState([]);
  const [activeLang, setActiveLang] = useState('en');
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    category_id:'', slug:'', sku:'', badge:'',
    price_eur:'', price_eur_original:'',
    stock:0, weight_grams:'', thc_percent:0, cbd_percent:'',
    origin:'', active:true, featured:false, is_new:false, sort_order:0,
    name:{}, description:{}, tags:[], images:[],
  });

  useEffect(() => {
    supabase.from('categories').select('id,name').order('sort_order')
      .then(({ data }) => setCategories(data || []));
    if (!isNew) {
      supabase.from('products').select('*').eq('id', id).single()
        .then(({ data }) => { if (data) setForm({ ...data, tags: data.tags||[], images: data.images||[] }); });
    }
  }, [id, isNew]);

  const set = (f, v) => setForm(x => ({ ...x, [f]: v }));
  const setL = (f, l, v) => setForm(x => ({ ...x, [f]: { ...(x[f]||{}), [l]: v } }));

  const handleSave = async () => {
    if (!form.name?.en) { showToast('English name is required', 'error'); return; }
    if (!form.price_eur || Number(form.price_eur) <= 0) { showToast('Price must be > 0', 'error'); return; }
    setSaving(true);
    const payload = {
      ...form,
      price_eur: Number(form.price_eur),
      price_eur_original: form.price_eur_original ? Number(form.price_eur_original) : null,
      stock: Number(form.stock),
      thc_percent: Number(form.thc_percent)||0,
      cbd_percent: form.cbd_percent ? Number(form.cbd_percent) : null,
      weight_grams: form.weight_grams ? Number(form.weight_grams) : null,
      slug: form.slug || form.name.en.toLowerCase().replace(/[^a-z0-9]+/g,'-').replace(/^-|-$/g,''),
      updated_at: new Date().toISOString(),
    };
    const { error } = isNew
      ? await supabase.from('products').insert(payload)
      : await supabase.from('products').update(payload).eq('id', id);
    setSaving(false);
    if (error) { showToast(error.message, 'error'); return; }
    showToast(isNew ? 'Product created!' : 'Product saved!', 'success');
    navigate('/admin/products');
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this product? Cannot be undone.')) return;
    await supabase.from('products').delete().eq('id', id);
    showToast('Product deleted', 'info');
    navigate('/admin/products');
  };

  const inp = (label, field, type='text', extra={}) => (
    <div style={{ marginBottom:16 }}>
      <div className="input-label">{label}</div>
      <input className="input-field" type={type} value={form[field]??''} onChange={e => set(field, e.target.value)} {...extra} />
    </div>
  );

  const Toggle = ({ field, label }) => (
    <label style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 0', borderBottom:'1px solid rgba(255,255,255,.05)', cursor:'pointer' }}>
      <span style={{ fontSize:'.88rem', color:'#ccc' }}>{label}</span>
      <div onClick={() => set(field, !form[field])} style={{ width:42, height:22, borderRadius:11, background: form[field]?'#4cca74':'#333', position:'relative', transition:'background .2s', flexShrink:0 }}>
        <div style={{ position:'absolute', top:3, left: form[field]?22:3, width:16, height:16, borderRadius:'50%', background:'#fff', transition:'left .2s' }} />
      </div>
    </label>
  );

  return (
    <div className="page-container" style={{ padding:'32px 24px', maxWidth:800 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <h2 style={{ fontFamily:"'Bebas Neue',sans-serif", fontSize:'1.8rem', letterSpacing:'.05em' }}>
          {isNew ? 'New Product' : 'Edit Product'}
        </h2>
        <div style={{ display:'flex', gap:10 }}>
          {!isNew && (
            <button onClick={handleDelete} style={{ background:'none', border:'1px solid rgba(224,80,80,.3)', borderRadius:20, color:'#e05050', padding:'8px 16px', cursor:'pointer', fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.78rem', fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase' }}>
              Delete
            </button>
          )}
          <button className="btn-primary" style={{ padding:'9px 20px', fontSize:'.82rem' }} onClick={handleSave} disabled={saving}>
            {saving ? <div className="spinner" style={{ width:18, height:18, borderWidth:2 }} /> : isNew ? 'Create Product' : 'Save Changes'}
          </button>
        </div>
      </div>

      {/* Lang tabs */}
      <div style={{ display:'flex', gap:4, marginBottom:20, background:'#111', borderRadius:12, padding:4, flexWrap:'wrap' }}>
        {LANGS.map(l => (
          <button key={l} onClick={() => setActiveLang(l)} style={{ flex:'0 0 auto', padding:'7px 14px', border:'none', borderRadius:8, background: activeLang===l?'#4cca74':'none', color: activeLang===l?'#000':'#555', fontFamily:"'Barlow Condensed',sans-serif", fontSize:'.78rem', fontWeight:700, letterSpacing:'.08em', textTransform:'uppercase', cursor:'pointer' }}>
            {l.toUpperCase()}
          </button>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:32 }} className="admin-product-edit-grid">
        {/* Left column */}
        <div>
          <div style={{ marginBottom:16 }}>
            <div className="input-label">Name ({activeLang.toUpperCase()}){activeLang==='en'?' *':''}</div>
            <input className="input-field" value={form.name?.[activeLang]||''} onChange={e => setL('name', activeLang, e.target.value)} />
          </div>
          <div style={{ marginBottom:20 }}>
            <div className="input-label">Description ({activeLang.toUpperCase()})</div>
            <textarea className="input-field" rows={5} value={form.description?.[activeLang]||''} onChange={e => setL('description', activeLang, e.target.value)} style={{ resize:'vertical' }} />
          </div>
          <div style={{ marginBottom:16 }}>
            <div className="input-label">Category *</div>
            <select className="input-field" value={form.category_id} onChange={e => set('category_id', e.target.value)} style={{ appearance:'none' }}>
              <option value="">Select category…</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name?.en}</option>)}
            </select>
          </div>
          {inp('Slug (URL — auto-generated if blank)', 'slug', 'text', { placeholder:'auto-generated' })}
          <div style={{ marginBottom:16 }}>
            <div className="input-label">Badge</div>
            <select className="input-field" value={form.badge||''} onChange={e => set('badge', e.target.value)} style={{ appearance:'none' }}>
              {BADGES.map(b => <option key={b.v} value={b.v}>{b.l}</option>)}
            </select>
          </div>
        </div>

        {/* Right column */}
        <div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <div><div className="input-label">Price EUR *</div><input className="input-field" type="number" step="0.01" min="0" value={form.price_eur} onChange={e => set('price_eur', e.target.value)} /></div>
            <div><div className="input-label">Original Price EUR</div><input className="input-field" type="number" step="0.01" min="0" value={form.price_eur_original||''} onChange={e => set('price_eur_original', e.target.value)} placeholder="for sale badge" /></div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <div><div className="input-label">Stock *</div><input className="input-field" type="number" min="0" value={form.stock} onChange={e => set('stock', e.target.value)} /></div>
            <div>{inp('SKU', 'sku')}</div>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
            <div><div className="input-label">THC % (max 0.2)</div><input className="input-field" type="number" step="0.01" min="0" max="0.2" value={form.thc_percent} onChange={e => set('thc_percent', e.target.value)} /></div>
            <div><div className="input-label">CBD %</div><input className="input-field" type="number" step="0.1" min="0" value={form.cbd_percent||''} onChange={e => set('cbd_percent', e.target.value)} /></div>
          </div>
          {inp('Origin (e.g. Switzerland)', 'origin')}
          {inp('Weight (grams)', 'weight_grams', 'number')}

          {/* Tags */}
          <div style={{ marginBottom:16 }}>
            <div className="input-label">Tags</div>
            {(form.tags||[]).map((tag, i) => (
              <div key={i} style={{ display:'flex', gap:8, marginBottom:6 }}>
                <input className="input-field" value={tag} onChange={e => { const t=[...form.tags]; t[i]=e.target.value; set('tags',t); }} placeholder={`Tag ${i+1}`} style={{ flex:1 }} />
                <button onClick={() => set('tags', form.tags.filter((_,idx)=>idx!==i))} style={{ background:'rgba(224,80,80,.1)', border:'none', borderRadius:8, color:'#e05050', padding:'0 12px', cursor:'pointer' }}>✕</button>
              </div>
            ))}
            <button onClick={() => set('tags', [...(form.tags||[]), ''])} style={{ background:'none', border:'1px dashed rgba(255,255,255,.15)', borderRadius:10, color:'#555', padding:'8px 16px', cursor:'pointer', fontSize:'.82rem', width:'100%', marginTop:4 }}>+ Add tag</button>
          </div>

          {/* Image URLs */}
          <div style={{ marginBottom:16 }}>
            <div className="input-label">Image URLs</div>
            {(form.images||[]).map((url, i) => (
              <div key={i} style={{ display:'flex', gap:8, marginBottom:6 }}>
                <input className="input-field" value={url} onChange={e => { const imgs=[...form.images]; imgs[i]=e.target.value; set('images',imgs); }} placeholder="https://…" style={{ flex:1 }} />
                <button onClick={() => set('images', form.images.filter((_,idx)=>idx!==i))} style={{ background:'rgba(224,80,80,.1)', border:'none', borderRadius:8, color:'#e05050', padding:'0 12px', cursor:'pointer' }}>✕</button>
              </div>
            ))}
            <button onClick={() => set('images', [...(form.images||[]), ''])} style={{ background:'none', border:'1px dashed rgba(255,255,255,.15)', borderRadius:10, color:'#555', padding:'8px 16px', cursor:'pointer', fontSize:'.82rem', width:'100%', marginTop:4 }}>+ Add image</button>
          </div>

          <Toggle field="active"   label="Visible to customers" />
          <Toggle field="featured" label="Featured on homepage" />
          <Toggle field="is_new"   label='Show "New" indicator' />
        </div>
      </div>
      <style>{`
        @media (max-width: 768px) {
          .admin-product-edit-grid { grid-template-columns: 1fr !important; gap: 0 !important; }
        }
      `}</style>
    </div>
  );
}